/**
 * Per-user AI-credit budgets + member "request more credits" — typed client.
 *
 * Mirrors the per-user budget cap + request-more surface shipped in
 * services-python/ai-orchestrator-service/src/routes/credits_api.py (gateway
 * prefix /api/v1/credits) and the `AIUserBudget` model
 * (services-python/ai-orchestrator-service/src/models/credits.py). A tenant
 * admin may cap how many credits a single member may consume per calendar
 * month; `monthly_credits <= 0` means "no personal cap" (only the shared
 * tenant wallet applies). This module is intentionally standalone (does not
 * import from `./credits`) so the new admin budgets page + request-more UI
 * have one dedicated, self-contained surface to depend on.
 *
 * Routes targeted (all under /api/v1/credits):
 *   GET  /user-budget    → getMyUserBudget()         (any authenticated user)
 *   GET  /user-budgets   → listUserBudgets()          (admin only, 403 otherwise)
 *   PUT  /user-budget    → setUserBudget(userId, cap) (admin only, 403 otherwise)
 *                           (0 clears the cap — there is no dedicated DELETE
 *                           route server-side, so clearUserBudget/deleteUserBudget
 *                           are thin PUT-with-0 wrappers.)
 *   POST /request-more   → requestMoreCredits(...)    (any authenticated user)
 *
 * Convention: 1 credit = 0.01 USD (see ai-credits.ts / credits.ts).
 */

import { api } from '@/lib/api/client';

const BASE = '/api/v1/credits';

/** The caller's own budget, or one row of an admin's set/clear response —
 *  the full shape returned by GET /user-budget and PUT /user-budget. */
export interface UserBudget {
  tenant_id: string;
  user_id: string;
  /** Monthly credit cap. 0 (or absent cap) means uncapped. */
  monthly_credits: number;
  /** Month-to-date SETTLED usage in credits. */
  used_this_month: number;
  /** `monthly_credits - used_this_month`, floored at 0; `null` when uncapped. */
  remaining: number | null;
  /** `monthly_credits > 0`. */
  capped: boolean;
}

/** One row of GET /user-budgets (admin listing) — a slightly leaner shape
 *  than {@link UserBudget}: no `tenant_id`/`capped`, but carries `updated_by`. */
export interface UserBudgetListItem {
  user_id: string;
  monthly_credits: number;
  used_this_month: number;
  remaining: number | null;
  updated_by: string | null;
}

/** The caller's own monthly AI-credit budget + month-to-date usage. */
export function getMyUserBudget(): Promise<UserBudget> {
  return api.get<UserBudget>(`${BASE}/user-budget`);
}

/** Every capped member for the tenant (admin only; throws `ApiError` 403 otherwise). */
export function listUserBudgets(): Promise<UserBudgetListItem[]> {
  return api.get<UserBudgetListItem[]>(`${BASE}/user-budgets`);
}

/**
 * Set (or clear, with `monthlyCredits <= 0`) a member's monthly budget cap.
 * Admin only — throws `ApiError` 403 otherwise. Returns the updated budget.
 */
export function setUserBudget(userId: string, monthlyCredits: number): Promise<UserBudget> {
  return api.put<UserBudget>(`${BASE}/user-budget`, {
    user_id: userId,
    monthly_credits: monthlyCredits,
  });
}

/**
 * Clear a member's monthly cap (no dedicated DELETE route server-side — this
 * is a `setUserBudget(userId, 0)` under the hood). Admin only.
 */
export function clearUserBudget(userId: string): Promise<UserBudget> {
  return setUserBudget(userId, 0);
}

/** Alias of {@link clearUserBudget} for callers that think in CRUD verbs. */
export const deleteUserBudget = clearUserBudget;

export interface RequestMoreCreditsBody {
  /** Optional free-text message to the tenant admins. */
  note?: string;
  /** Optional credits amount the member is asking for. */
  amount?: number;
}

export interface RequestMoreCreditsResult {
  ok: boolean;
  requested_by?: string;
  amount?: number | null;
}

/**
 * Member "ask an admin for more credits" — notifies tenant admins via the
 * collab notification bell (best-effort server-side). Always resolves
 * `{ok: true}` on a 2xx response; the request is logged server-side even if
 * the notification transport is unavailable.
 */
export function requestMoreCredits(
  body: RequestMoreCreditsBody = {},
): Promise<RequestMoreCreditsResult> {
  return api.post<RequestMoreCreditsResult>(`${BASE}/request-more`, body);
}
